import { formatNumber } from 'utils/number';
import type { DatasetFacets } from '../types';

/** One bin of a pooled value histogram. */
export interface ValueHistogramBin {
  x0: number;
  x1: number;
  count: number;
}

/** A pooled value histogram facet, as the list endpoint returns it. */
export interface ValueHistogramFacet {
  min: number;
  max: number;
  units: string;
  bins: ValueHistogramBin[];
}

/** A value range to apply; an open end is ±Infinity. */
export interface ValueRange {
  low: number;
  high: number;
}

/**
 * Reads a value histogram facet, which (unlike bucket facets) is null when no
 * matching map has stats yet.
 * @param {DatasetFacets | null | undefined} facets The facets.
 * @param {string} key The facet key, e.g. 'valueHistogram'.
 * @return {ValueHistogramFacet | null} The histogram, or null when absent.
 */
export function readValueHistogram(
  facets: DatasetFacets | null | undefined,
  key: string
): ValueHistogramFacet | null {
  const facet = (facets as Record<string, unknown> | null | undefined)?.[
    key
  ] as Partial<ValueHistogramFacet> | null | undefined;
  if (
    !facet ||
    !Number.isFinite(facet.min) ||
    !Number.isFinite(facet.max) ||
    !Array.isArray(facet.bins)
  ) {
    return null;
  }
  return facet as ValueHistogramFacet;
}

/**
 * Parses a staged range bound. Inputs stage strings; blank or non-numeric is unset.
 * @param {unknown} value The staged value.
 * @return {number | null} The number, or null when unset.
 */
export function parseRangeBound(value: unknown): number | null {
  if (value === undefined || value === null || value === '') return null;
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
}

/**
 * Orders a pair of bounds, so a range typed back to front (or passed through on the
 * way to a longer number) still means the span between them.
 * @param {number | null} low The staged min.
 * @param {number | null} high The staged max.
 * @return {Array<number | null>} The bounds, low first.
 */
export function orderedBounds<T extends number | null>(
  low: T,
  high: T
): [T, T] {
  if (low !== null && high !== null && low > high) return [high, low];
  return [low, high];
}

/**
 * Formats a map value: whole numbers with commas from 1,000 up, otherwise up to
 * two decimals ("5,872", "676.7").
 * @param {number} value The value.
 * @return {string} The label.
 */
export function formatRangeValue(value: number): string {
  return Math.abs(value) >= 1_000
    ? formatNumber(value, 0, 0)
    : formatNumber(value, 0, 2);
}

/**
 * The chip label for a value range: "1,500 – 3,000 ft", "≥ 1,500 ft" or "≤ 3,000 ft".
 * @param {unknown} min The staged min.
 * @param {unknown} max The staged max.
 * @param {string} [units] Units from the facet, when loaded.
 * @return {string | null} The label, or null when neither end is set.
 */
export function valueRangeLabel(
  min: unknown,
  max: unknown,
  units?: string
): string | null {
  const [low, high] = orderedBounds(parseRangeBound(min), parseRangeBound(max));
  const suffix = units ? ` ${units}` : '';
  if (low !== null && high !== null) {
    return `${formatRangeValue(low)} – ${formatRangeValue(high)}${suffix}`;
  }
  if (low !== null) return `≥ ${formatRangeValue(low)}${suffix}`;
  if (high !== null) return `≤ ${formatRangeValue(high)}${suffix}`;
  return null;
}

/**
 * The applied range from a payload, with an unset end left open.
 * @param {Record<string, unknown> | undefined} payload The committed payload.
 * @param {string} minParam The min param.
 * @param {string} maxParam The max param.
 * @return {ValueRange | null} The range, or null when neither end is set.
 */
export function valueRangeFrom(
  payload: Record<string, unknown> | undefined,
  minParam: string,
  maxParam: string
): ValueRange | null {
  const [low, high] = orderedBounds(
    parseRangeBound(payload?.[minParam]),
    parseRangeBound(payload?.[maxParam])
  );
  if (low === null && high === null) return null;
  return { low: low ?? -Infinity, high: high ?? Infinity };
}

const RANGE_DECIMALS = 3;

/**
 * A value range rounded outward to three decimals, as the inputs show it, so an
 * edge value never falls outside it.
 * @param {[number, number]} range Min and max.
 * @return {[number, number]} The rounded range.
 */
export function roundRangeOutward([min, max]: [number, number]): [
  number,
  number,
] {
  const scale = 10 ** RANGE_DECIMALS;
  return [Math.floor(min * scale) / scale, Math.ceil(max * scale) / scale];
}
